import type { StandardSchemaV1 } from "@standard-schema/spec";
import { useForm } from "@tanstack/react-form";
import { useCallback, useEffect, useRef, useState } from "react";

import { sentryService } from "../sentry";

// The outcome of a persistence attempt. Expected, recoverable failures — invalid
// field values the user can fix — are values on the success channel, so the
// consumer maps its mutation's typed result here instead of the hook dissecting a
// thrown `unknown`. Throwing is reserved for unexpected failures (network/server),
// which surface as a generic form-level error.
//
// Optional `TData` on `ok`: default `never` keeps `{ status: "ok" }` (and
// `data?: never` rejects a stray `data` field). When set, `data` is required so
// callers see `TData`, not `TData | undefined`. Same `[T] extends [never]`
// distribution guard as flow-engine.
export type SubmitOutcome<TData = never> =
  | ([TData] extends [never]
      ? { status: "ok"; data?: never }
      : { status: "ok"; data: TData })
  | {
      status: "invalid";
      formError?: string;
      fieldErrors?: Record<string, string>;
    };

// Ok arm passed to `onSubmitSuccess`. Passing the whole arm (not plucked
// `data`) stays cast-free after `status === "ok"` narrows `outcome`.
type SubmitOk<TData> = Extract<SubmitOutcome<TData>, { status: "ok" }>;

type UseFormEngineConfig<TValues, TOutput, TData = never> = {
  // Snapshot taken once at mount (the pre-mount data-loading boundary).
  defaultValues: TValues;
  // Identity of the source record (e.g. the selected patient). A change re-seeds
  // from `defaultValues`; a same-source `defaultValues` change does not.
  sourceKey?: string | number;
  // Zod (or any Standard Schema) whose *input* type equals TValues — derive
  // draft types with `z.input<typeof schema>` (wrap sentinel-empty fields in
  // `Partial`). The schema's *output* flows to `onSubmit`: transforms and
  // type-predicate refinements arrive parsed and narrowed.
  schema: StandardSchemaV1<TValues, TOutput>;
  // Persist the parsed values and return a `SubmitOutcome`. `NoInfer` stops a
  // bare `{ status: "ok" }` from inferring `TData` as `undefined` (via the
  // optional `data?: never` field), which would make `data` required again.
  onSubmit: (
    values: TOutput,
  ) => SubmitOutcome<NoInfer<TData>> | Promise<SubmitOutcome<NoInfer<TData>>>;
  // Called only after a successful submit — the flow advances here.
  onSubmitSuccess?: (values: TOutput, outcome: SubmitOk<TData>) => void;
};

// The one user-facing copy for unexpected submit failures. Raw error messages
// (Apollo/network/server strings) must never reach the form — expected,
// user-fixable failures carry their own copy via `SubmitOutcome`.
export const SUBMIT_ERROR_MESSAGE = "Something went wrong, please try again.";

export function useFormEngine<TValues, TOutput = TValues, TData = never>({
  defaultValues,
  sourceKey,
  schema,
  onSubmit,
  onSubmitSuccess,
}: UseFormEngineConfig<TValues, TOutput, TData>) {
  // Seeded once at mount (a later same-source `defaultValues` change must not
  // overwrite in-progress edits), then kept in lockstep with every deliberate
  // reset below. TanStack's `useForm` re-syncs `defaultValues` against
  // `form.options.defaultValues` on every render (`FormApi.update`); `reset()`
  // durably rewrites `form.options.defaultValues` too, so if this ref stayed
  // frozen at the mount-time snapshot, that very next sync would see a stale
  // `defaultValues` argument and silently revert the reset it just applied.
  const initialValuesRef = useRef(defaultValues);
  const [formError, setFormError] = useState<string | null>(null);

  const form = useForm({
    defaultValues: initialValuesRef.current,
    validators: { onChange: schema, onSubmit: schema },
    // A rejected submit attempt must surface why: handleSubmit bails on an
    // already-invalid form before its submit-cause validation runs, leaving
    // the fields' `errorMap.onSubmit` slots empty — adapters gating display on
    // blur-or-submit would show nothing and the tap would look dead.
    // Re-validating with the submit cause routes errors into those slots.
    onSubmitInvalid: () => {
      void form.validate("submit");
    },
    onSubmit: async ({ value }) => {
      setFormError(null);
      try {
        const validated = schema["~standard"].validate(value);
        const parsed =
          validated instanceof Promise ? await validated : validated;
        if (parsed.issues !== undefined) {
          // The onSubmit validator vetoes invalid drafts before this runs; a
          // failure here is an engine-contract bug, not user input.
          throw new Error(
            "schema rejected values that passed submit validation",
          );
        }
        const outcome = await onSubmit(parsed.value);
        if (outcome.status === "ok") {
          onSubmitSuccess?.(parsed.value, outcome);
          return;
        }
        setFormError(outcome.formError ?? null);
        // Routes `fields` to registered fields by name and ignores keys with no
        // mounted field — the off-section tolerance the spec needs.
        form.setErrorMap({
          onSubmit: {
            form: outcome.formError,
            fields: outcome.fieldErrors ?? {},
          },
        });
      } catch (err) {
        // Unexpected failures only — expected ones return `invalid` above.
        // The engine owns reporting + copy so consumers don't wrap onSubmit
        // in their own try/catch.
        sentryService.captureException(err);
        setFormError(SUBMIT_ERROR_MESSAGE);
      }
    },
  });

  // Imperative re-seed for a caller that already knows it needs a fresh
  // baseline (e.g. recovering from a server-side conflict) — bypasses the
  // `sourceKey` identity check below, which only fires on a genuine source
  // change.
  const reset = useCallback(
    (values: TValues) => {
      initialValuesRef.current = values;
      form.reset(values);
    },
    [form],
  );

  const seededSourceKey = useRef(sourceKey);
  useEffect(() => {
    if (sourceKey === undefined || sourceKey === seededSourceKey.current) {
      return;
    }
    seededSourceKey.current = sourceKey;
    reset(defaultValues);
    setFormError(null);
  }, [sourceKey, defaultValues, reset]);

  // Submit through this, not `form.handleSubmit` directly, on any form that maps
  // server rejections to `fieldErrors`. TanStack refuses to run `onSubmit` while
  // onSubmit-map field errors persist, so the clear-before-submit has to happen
  // *before* handleSubmit — a form rejected once could never be resubmitted if
  // the clear lived inside onSubmit (it would never be reached).
  const submit = useCallback(() => {
    setFormError(null);
    form.setErrorMap({ onSubmit: { fields: {} } });
    return form.handleSubmit();
  }, [form]);

  return { form, formError, submit, reset };
}
